import { Skeleton } from "@mui/material";
import Tab from "@mui/material/Tab";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import CountCircle from "~/src/components/CountCirle";
import { useClientState } from "~/src/hooks/useClientState";
import useOversikt from "~/src/hooks/useOversikt";
import BottomLine from "../../../src/components/BottomLine";
import { CalendarIcon, TargetIcon } from "../../../src/components/Icons/Icons";
import BoldText from "../../../src/components/Text/BoldText";
import { Header, TextVariant } from "../../../src/components/Text/Text";
import { StorageItem } from "../../../src/contexts/StorageContext/StorageProvider";
import { useStateful } from "../../../src/hooks/useStateful";
import { IndividResponse, JaktdagResponse } from "../../api/types";
import JaktdagCard from "../../components/JaktdagCard/JaktdagCard";
import JaktomradePicker from "../../components/JaktomradePicker/JaktomradePicker";
import SkuttDyrCard from "../../components/SkuttDyrCard/SkuttDyrCard";
import TabPanel from "../../components/Tab/TabPanel";
import TabsDefault from "../../components/Tab/TabsDefault";
import FilterCombobox from "./Components/FilterCombobox";
import TomtResultat from "./Components/TomtResultat";

export default function Oversikt(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const missingAreaSelectionText = t("pages.oversikt.missingAreaSelection");
  const noResultText = t("pages.oversikt.noRegistrations");
  const noResultWFiltersText = t("pages.oversikt.noRegistrationsMatchingFilters");
  const clientState = useClientState();

  const {
    loading,
    handleSelectJaktomrade,
    jaktomrade,
    valgtOmradekode,
    showMissingSelection,
    showArtFilter,
    showMineRegOption,
    handleFilter,
    showJaktdagTab,
    hasJaktdager,
    jaktdager,
    storage,
    hasSkutteDyr,
    skutteDyr,
    hasJaktdag,
    location,
    selectedFilters,
    noFilteredResultsJaktdager,
    noFilteredResultsIndivider,
  } = useOversikt();

  const tabValue = useStateful(location.state?.activeTab ?? 0);

  useEffect(() => {
    if (jaktomrade.value?.data && !hasJaktdag(jaktomrade.value)) {
      tabValue.set(1);
    }
  }, [jaktomrade.value]);

  return (
    <div className="h-full">
      <div className="h-full">
        <div className="bg-ldir-blue-800 p-4">
          <JaktomradePicker
            onDarkBg={true}
            onChange={handleSelectJaktomrade}
            defaultOmradekode={jaktomrade.value?.id || valgtOmradekode}
          />
        </div>
        <div className="bg-ldir-blue-800 px-4 pb-4">
          <FilterCombobox
            disabled={loading || showMissingSelection}
            selected={selectedFilters.value}
            onToggle={handleFilter}
            showElgHjortOptions={showArtFilter ?? false}
            showMineRegOption={showMineRegOption}
          />
        </div>
        {showMissingSelection && !loading ? (
          <TomtResultat>
            <BoldText
              text={missingAreaSelectionText}
              numBoldWords={5}
              variant={TextVariant.xxxlarge}
            />
          </TomtResultat>
        ) : (
          <>
            <div key={tabValue.value} className="h-[calc(100%-242px)]">
              <BottomLine className="border-ldir-blue-pale/25 bg-ldir-blue-800" />
              <TabsDefault
                value={tabValue.value}
                onChange={(_e, newValue) => tabValue.set(newValue)}
              >
                <Tab
                  aria-expanded={tabValue.value === 0}
                  style={{ paddingLeft: "8px", paddingRight: "8px", fontWeight: 400 }}
                  aria-label={t("pages.oversikt.jaktdager")}
                  tabIndex={tabValue.value === 0 ? -1 : 0}
                  label={
                    <div className="flex items-center flex-wrap justify-center w-full max-w-fit">
                      <CalendarIcon sizeClassName="h-6 w-6" className="mr-2 icon" />
                      {tabValue.value === 0 ? (
                        <Header headerLvl={2} className="normal-case font-normal">
                          {t("pages.oversikt.jaktdager")}
                        </Header>
                      ) : (
                        <span className="text-xl normal-case">{t("pages.oversikt.jaktdager")}</span>
                      )}
                      <CountCircle
                        count={jaktdager.data?.length}
                        color="light"
                        className="ml-2 count"
                      />
                    </div>
                  }
                  className={!showJaktdagTab ? "hidden" : "doubleTab"}
                />
                <Tab
                  aria-expanded={tabValue.value === 1}
                  style={{
                    paddingLeft: !showJaktdagTab ? "16px" : "8px",
                    paddingRight: "8px",
                    fontWeight: 400,
                  }}
                  aria-label={t("pages.oversikt.skutteDyr")}
                  className={!showJaktdagTab ? "items-start border-b-0" : "doubleTab"}
                  tabIndex={tabValue.value === 1 ? -1 : 0}
                  label={
                    <div className="flex items-center flex-wrap justify-center w-full max-w-fit">
                      <TargetIcon className="mr-2 icon" />
                      {tabValue.value === 1 ? (
                        <Header headerLvl={2} className="normal-case font-normal">
                          {t("pages.oversikt.skutteDyr")}
                        </Header>
                      ) : (
                        <span className="text-xl normal-case">{t("pages.oversikt.skutteDyr")}</span>
                      )}
                      <CountCircle
                        count={skutteDyr.data?.length}
                        color="light"
                        className="ml-2 count"
                      />
                    </div>
                  }
                />
              </TabsDefault>
              <TabPanel
                value={tabValue.value}
                index={0}
                className={!showJaktdagTab ? "hidden" : ""}
              >
                {hasJaktdager && jaktdager.data?.length ? (
                  <div className="p-4 flex flex-col gap-4">
                    {jaktdager.data?.map((jaktdag: StorageItem<JaktdagResponse>, index) => (
                      <JaktdagCard
                        key={index}
                        data={jaktdag.data}
                        syncStatus={jaktdag.syncStatus}
                        storage={storage}
                        isLoading={loading}
                        queryClient={queryClient}
                        clientState={clientState}
                      />
                    ))}
                    <SkeletonLoaders isLoading={loading} />
                  </div>
                ) : loading ? (
                  <div className="flex flex-col gap-4 p-4">
                    <SkeletonLoaders isLoading={loading} />
                  </div>
                ) : (
                  <TomtResultat>
                    {noFilteredResultsJaktdager ? (
                      <BoldText
                        text={noResultWFiltersText}
                        specificWords={[0, 1, 5, 6]}
                        variant={TextVariant.xxxlarge}
                      />
                    ) : (
                      <BoldText
                        text={noResultText}
                        numBoldWords={3}
                        variant={TextVariant.xxxlarge}
                      />
                    )}
                  </TomtResultat>
                )}
              </TabPanel>
              <TabPanel value={tabValue.value} index={1}>
                {hasSkutteDyr && skutteDyr.data?.length ? (
                  <div className="p-4 flex flex-col gap-4">
                    {skutteDyr.data?.map((skuttDyr: StorageItem<IndividResponse>, index) => (
                      <SkuttDyrCard
                        key={index}
                        skuttDyr={skuttDyr.data}
                        syncStatus={skuttDyr.syncStatus}
                        isLoading={loading}
                        queryClient={queryClient}
                        clientState={clientState}
                        storage={storage}
                      />
                    ))}
                    <SkeletonLoaders isLoading={loading} />
                  </div>
                ) : loading ? (
                  <div className="flex flex-col gap-4 p-4">
                    <SkeletonLoaders isLoading={loading} />
                  </div>
                ) : (
                  <TomtResultat>
                    {noFilteredResultsIndivider ? (
                      <BoldText
                        text={noResultWFiltersText}
                        specificWords={[0, 1, 5, 6]}
                        variant={TextVariant.xxxlarge}
                      />
                    ) : (
                      <BoldText
                        text={noResultText}
                        numBoldWords={3}
                        variant={TextVariant.xxxlarge}
                      />
                    )}
                  </TomtResultat>
                )}
              </TabPanel>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function SkeletonLoaders({ taller, isLoading }: { taller?: boolean; isLoading?: boolean }) {
  return isLoading ? (
    <div className="flex flex-col gap-2">
      <Skeleton
        sx={{ bgcolor: "#E5EEEE" }}
        variant="rounded"
        width={"full"}
        height={taller ? 180 : 100}
      />
      <Skeleton
        sx={{ bgcolor: "#E3EDF0" }}
        variant="rounded"
        width={"full"}
        height={taller ? 180 : 100}
      />
      <Skeleton
        sx={{ bgcolor: "#E5EFF1" }}
        variant="rounded"
        width={"full"}
        height={taller ? 180 : 100}
      />
    </div>
  ) : (
    <></>
  );
}
